/**
 * collection-products-fetch.js
 *
 * Async Section Rendering API paginator for the PDP configurator.
 *
 * Problem solved: Shopify silently caps Liquid `paginate by 1000` at ~250 products.
 * Frame families with 300+ SKUs lose options because some products never appear
 * in the server-rendered JSON blob.
 *
 * Solution:
 *   1. product-selections.liquid seeds collection.products.all with the current
 *      product only (instant first render) and adds data-collection-handle on the
 *      script tag.
 *   2. This module detects that attribute, checks sessionStorage for a cached full
 *      product list, and if absent fires paginated fetches via the Section Rendering
 *      API (50 products/request, well under Shopify's per-request cap).
 *   3. Once all pages are merged, collection.products.all is replaced in-place and
 *      the filter pipeline re-runs — no change to current selections or product.
 *   4. sessionStorage keeps the merged array for the rest of the browser session so
 *      subsequent page views cost zero network requests.
 *
 * Integration surface:
 *   - Reads:  script[type="collection/products"][data-collection-handle]  (ST-3)
 *   - Writes: window.collection.products.all                              (ST-4 compatible)
 *   - Events: collection:fetch:start | collection:fetch:complete |
 *             collection:fetch:error                                      (ST-5 loading state)
 *   - Global: window.collectionProductsFetch.run(handle)                 (callable from ST-4)
 *
 * Section Rendering API endpoint:
 *   GET /collections/{handle}?section_id=collection-products-data&page={n}
 *
 *   Response HTML wrapper:
 *     <div id="shopify-section-collection-products-data">
 *       { "products": [...], "has_next_page": true, "total_pages": 6 }
 *     </div>
 */

(function () {

  // ─── Cache ───────────────────────────────────────────────────────────────────

  /**
   * Bump CACHE_VER when the data shape contract changes so stale sessionStorage
   * entries from old deployments are automatically ignored.
   */
  var CACHE_VER = 1;
  var CACHE_PREFIX = 'caddis_col_v' + CACHE_VER + '_';

  function cacheKey(handle) {
    return CACHE_PREFIX + handle;
  }

  /**
   * Returns the cached product array for handle, or null if absent / invalid.
   * Never throws — storage errors degrade to a cache miss.
   */
  function readCache(handle) {
    try {
      var raw = sessionStorage.getItem(cacheKey(handle));
      if (!raw) return null;
      var entry = JSON.parse(raw);
      if (!Array.isArray(entry.products) || entry.products.length === 0) return null;
      return entry.products;
    } catch (_) {
      return null;
    }
  }

  /**
   * Persists products to sessionStorage. Silently swallows quota errors so a
   * very large collection degrades gracefully (fetch still works, just not cached).
   */
  function writeCache(handle, products) {
    try {
      sessionStorage.setItem(cacheKey(handle), JSON.stringify({ products: products }));
    } catch (_) {
      console.warn('[collection-products-fetch] sessionStorage write failed for "' + handle + '" — cache disabled for this session');
    }
  }

  // ─── Section Rendering API ────────────────────────────────────────────────────

  /**
   * Fetches one page from the Section Rendering API and returns the parsed
   * JSON payload { products, has_next_page, total_pages }.
   *
   * @param {string} handle  Collection handle
   * @param {number} page    1-based page number
   * @returns {Promise<{products: Array, has_next_page: boolean, total_pages: number}>}
   */
  function fetchPage(handle, page) {
    var url = '/collections/' + encodeURIComponent(handle)
              + '?section_id=collection-products-data&page=' + page;

    return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (res) {
        if (!res.ok) {
          throw new Error('HTTP ' + res.status + ' fetching page ' + page);
        }
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var wrapper = doc.querySelector('#shopify-section-collection-products-data');

        if (!wrapper) {
          throw new Error(
            'Section wrapper #shopify-section-collection-products-data not found on page '
            + page + '. Verify sections/collection-products-data.liquid is present in the theme.'
          );
        }

        return JSON.parse(wrapper.textContent.trim());
      });
  }

  /**
   * Iterates pages 1..N until has_next_page is false and returns the merged
   * product array. Requests are sequential (not parallel) to avoid hammering
   * Shopify's servers and to keep memory overhead low.
   *
   * @param {string} handle
   * @returns {Promise<Array>}
   */
  function fetchAllPages(handle) {
    var all = [];
    var page = 1;

    function next() {
      return fetchPage(handle, page).then(function (data) {
        Array.prototype.push.apply(all, data.products);

        if (page === 1) {
          console.log(
            '[collection-products-fetch] "' + handle + '": '
            + data.total_pages + ' page(s) to fetch ('
            + (data.total_pages * 50) + ' max products)'
          );
        }

        if (data.has_next_page) {
          page++;
          return next();
        }

        return all;
      });
    }

    return next();
  }

  // ─── Pipeline re-run ─────────────────────────────────────────────────────────

  /**
   * Re-runs the read-only portion of the filter pipeline after products.all
   * has been replaced. Intentionally does NOT call filter.products() to avoid
   * resetting collection.product or pushing a new history entry — the current
   * page, variant, and selections are preserved. filter.values() recalculates
   * which option values are available across the full 300+ product dataset;
   * render() refreshes all selector components.
   */
  function reRunPipeline() {
    if (!window.collection || typeof collection.filter === 'undefined') return;

    collection.filter.values()
      .then(function () {
        return collection.render();
      })
      .catch(function (err) {
        console.error('[collection-products-fetch] pipeline re-run error', err);
      });
  }

  // ─── Custom events ────────────────────────────────────────────────────────────

  /**
   * Dispatches a CustomEvent on document so ST-5 (loading state) and any other
   * listeners can react without coupling to this module's internals.
   *
   * Events:
   *   collection:fetch:start    { handle }
   *   collection:fetch:complete { handle, source: 'cache'|'fetch', count }
   *   collection:fetch:error    { handle, reason }
   */
  function dispatch(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail: detail, bubbles: false }));
  }

  // ─── In-flight deduplication ─────────────────────────────────────────────────

  /**
   * Tracks handles currently being fetched so concurrent calls to run() for the
   * same handle (e.g. from _startBackgroundFetch() in index.js and the bootstrap
   * setTimeout fallback) resolve against the same Promise instead of firing
   * duplicate network requests.
   */
  var _inFlight = {};

  // ─── Main run logic ───────────────────────────────────────────────────────────

  /**
   * Entry point. Checks cache, fetches all pages if needed, replaces
   * collection.products.all, and re-runs the filter pipeline.
   *
   * Safe to call multiple times for the same handle — a second call while a
   * fetch is in progress returns the same Promise; a second call after the
   * fetch completes returns from cache immediately.
   *
   * @param {string} handle  Shopify collection handle
   * @returns {Promise<void>}
   */
  function run(handle) {
    if (!handle || typeof handle !== 'string') {
      return Promise.reject(new Error('[collection-products-fetch] run() called without a valid handle'));
    }

    if (_inFlight[handle]) {
      console.log('[collection-products-fetch] run() already active for "' + handle + '" — skipping duplicate call');
      return _inFlight[handle];
    }

    dispatch('collection:fetch:start', { handle: handle });

    // ── Cache hit path ────────────────────────────────────────────────────────
    var cached = readCache(handle);
    if (cached) {
      console.log(
        '[collection-products-fetch] cache hit for "' + handle
        + '" (' + cached.length + ' products)'
      );
      collection.products.all = cached;
      reRunPipeline();
      dispatch('collection:fetch:complete', { handle: handle, source: 'cache', count: cached.length });
      return Promise.resolve();
    }

    // ── Network fetch path ────────────────────────────────────────────────────
    console.log('[collection-products-fetch] cache miss — fetching "' + handle + '"');

    _inFlight[handle] = fetchAllPages(handle)
      .then(function (products) {
        delete _inFlight[handle];

        if (products.length === 0) {
          console.warn(
            '[collection-products-fetch] fetch returned 0 products for "'
            + handle + '" — keeping seed data'
          );
          dispatch('collection:fetch:error', { handle: handle, reason: 'empty_response' });
          return;
        }

        writeCache(handle, products);

        console.log(
          '[collection-products-fetch] fetched ' + products.length
          + ' products for "' + handle + '"'
        );

        collection.products.all = products;
        reRunPipeline();
        dispatch('collection:fetch:complete', { handle: handle, source: 'fetch', count: products.length });
      })
      .catch(function (err) {
        delete _inFlight[handle];
        console.error(
          '[collection-products-fetch] fetch failed for "' + handle
          + '" — keeping seed data.',
          err
        );
        dispatch('collection:fetch:error', { handle: handle, reason: err.message });
      });

    return _inFlight[handle];
  }

  // ─── Bootstrap ───────────────────────────────────────────────────────────────

  /**
   * Looks for the data-collection-handle attribute that ST-3 adds to the
   * script[type="collection/products"] element. If absent, this is not a
   * configurator PDP and the module exits silently.
   *
   * Scheduling: collection.init() also runs on DOMContentLoaded. We use a
   * setTimeout(0) after the DOMContentLoaded callback to let collection.init()'s
   * synchronous setup (selectors parse, seed JSON parse, products.all set) complete
   * before we begin the background fetch. The network requests will take far longer
   * than the subsequent async filter-pipeline Promise chain, so by the time run()
   * replaces products.all the first render from the seed is long finished.
   */
  function bootstrap() {
    var scriptEl = document.querySelector('script[type="collection/products"][data-collection-handle]');
    if (!scriptEl) return;

    var handle = scriptEl.getAttribute('data-collection-handle');
    if (!handle) return;

    function start() {
      // Defer one tick so collection.init()'s synchronous setup runs first.
      setTimeout(function () { run(handle); }, 0);
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start, { once: true });
    } else {
      start();
    }
  }

  // ─── Public API ──────────────────────────────────────────────────────────────

  /**
   * Exposed on window so ST-4 (components/product-selection/index.js two-phase
   * init) can call collectionProductsFetch.run(handle) directly, and so the
   * integration can be tested from the browser console.
   */
  window.collectionProductsFetch = {
    run: run,
    readCache: readCache,
    writeCache: writeCache
  };

  bootstrap();

})();
